import { apiCall } from '@/lib/api-client';
import type { ToursState } from '@/features/tour/types';

/**
 * Auth API — login + signup + logout + current-user lookup. Cookies are
 * httpOnly + same-origin so the apiCall helper's `credentials: 'same-origin'`
 * default does the right thing without any client-side token juggling.
 */

/** Role string returned by ``/api/auth/me``. The ``platform_admin`` value
 * is reserved for Regara-internal users (separate auth surface); it's
 * included for completeness but customer-facing components should treat
 * it the same as ``admin`` for permission decisions. */
export type UserRole = 'admin' | 'editor' | 'reviewer' | 'viewer' | 'platform_admin';

export interface AuthMeResponse {
  id: number;
  email: string;
  /** The user's role SET. admin/viewer are exclusive; editor/reviewer combine.
   * ``platform_admin`` is Regara-internal (separate auth surface). */
  roles: UserRole[];
  /** Display name derived server-side from the local-part of the email
   * (capitalised). Always a non-empty string per backend contract. */
  name: string;
  /** The user's company per their customer-account record. Empty
   * string when the user hasn't completed the customer wizard yet. */
  company: string;
  /** True when this user is the original founder of the customer
   * (admin AND ``invited_by_user_id IS NULL`` on the backend). Drives
   * the OWNER badge in the header strip. Owner has the same backend
   * permissions as a regular admin plus team-protection rules
   * (can't be promoted/demoted/removed). */
  is_founder: boolean;
  /** Account creation timestamp (ISO 8601) — drives "Member since" on /profile. */
  created_at: string;
  /** Cosmetic UI preferences, persisted server-side (project rule: no
   * browser storage). Empty object when nothing saved yet. */
  ui_prefs: UiPrefs;
  /** Effective run-completion-email state per agent (kill-switch AND not demo
   * mode) — each dispatch UI gates its "we'll email you" promise on its flag. */
  run_email_enabled: boolean;
  protocol_run_email_enabled: boolean;
  /** The admin switch alone — a "turn it on" hint only makes sense when this is true. */
  run_email_admin_enabled: boolean;
  protocol_run_email_admin_enabled: boolean;
  /** True when the user has not yet accepted the current terms version.
   * The onboarding gate (Task 8) reads this and routes to the terms page
   * before allowing access to the rest of the app. */
  terms_required: boolean;
  /** App-level: whether the tech-spec document upload runs LLM extraction. When
   * false, an upload is stored but not extracted — drives the upload-page copy.
   * Optional so existing test fixtures needn't set it; absent → treated as on. */
  tech_spec_extraction_enabled?: boolean;
  /** Admin Settings "Simulators" switch — false greys the nav tab out. */
  simulators_enabled?: boolean;
  /** Size cap that will actually apply to this user's next tech-spec upload.
   *  Server-computed (direct-to-storage cap vs the smaller through-app one), so
   *  the figure shown is the figure enforced. */
  tech_spec_upload_max_bytes?: number;
  /** Per-product supporting-document ceilings (admin dials). Needed on the create
   *  screen, which stages files before a product exists to count against. */
  supporting_docs_max_files?: number;
  supporting_docs_max_bytes?: number;
  supporting_docs_max_file_bytes?: number;
  dossier_upload_max_bytes?: number;
  /** Vault upload dials (admin Settings tab): most files one folder/multi-file pick may carry,
   *  and the largest single file. The upload dialog states and enforces these. */
  vault_upload_max_files?: number;
  vault_upload_max_file_bytes?: number;
  /** The zip import's own limits (server constants, not dials) — a separate rule set from the
   *  per-file caps, because a zip travels through the app server. The upload dialog states the
   *  packed and unpacked rules apart, from these, so it never quotes an unenforced number. */
  vault_archive_max_bytes?: number;
  vault_archive_max_unpacked_bytes?: number;
  vault_archive_max_files?: number;
  /** AI data-use track for this account: "non_training" (default) | "training".
   * Admin-assigned per company at invite; drives which data-use agreement the
   * terms accept screen shows + records. */
  ai_training_track: 'non_training' | 'training';
  /** Per-user display prefs. Every server timestamp is UTC; these control how
   * THIS user renders them (set in Profile & Settings). Default "UTC" /
   * "YYYY-MM-DD" until the user customizes their own view. */
  timezone: string;
  date_format: string;
  /** Clock style: "24h" (default) | "12h". Independent of date_format. */
  time_format: string;
  /** The user's own per-agent switches ({ strategy: false }; missing = on). The
   * *_run_email_enabled flags already fold these in — read this only for the hint. */
  agent_email_prefs: Record<string, boolean>;
}

/** Known cosmetic preference keys — mirror backend UiPrefsUpdateRequest. */
export interface UiPrefs {
  sidebar_collapsed?: boolean;
  /** Onboarding tour progress, keyed by tour id. Persisted per step (resume). */
  tours?: ToursState;
}

/** PATCH /api/auth/me/ui-prefs — partial merge; omitted keys keep their
 * stored value. Returns the full refreshed /me payload. */
export async function updateUiPrefs(prefs: UiPrefs): Promise<AuthMeResponse> {
  return apiCall<AuthMeResponse>('/api/auth/me/ui-prefs', {
    method: 'PATCH',
    json: prefs,
  });
}

/** Admin-only gate. Drives every "Edit / Archive / Submit / Start Run"
 * affordance and the Team & invites nav entry. Editor + viewer share
 * the same read-only set today — the collaboration page split that
 * differentiates them is future work. */
export function isAdmin(user: { roles: UserRole[] } | null | undefined): boolean {
  return user?.roles?.includes('admin') ?? false;
}

/** True when the user may edit Vault documents (admin or editor). */
export function canEdit(user: { roles: UserRole[] } | null | undefined): boolean {
  const r = user?.roles ?? [];
  return r.includes('admin') || r.includes('editor');
}

/** True when the user may approve/sign Vault documents (admin or reviewer). */
export function canReview(user: { roles: UserRole[] } | null | undefined): boolean {
  const r = user?.roles ?? [];
  return r.includes('admin') || r.includes('reviewer');
}

/** Label rendered in the header strip + anywhere the UI wants to
 * surface "what role is this user". OWNER outranks ADMIN visually but
 * they share backend permissions. ``null`` for the anonymous / loading
 * state so the caller can omit the chip while ``useMe()`` resolves. */
export type DisplayRole = 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER' | 'REVIEWER';

export function displayRole(
  user: { roles: UserRole[]; is_founder?: boolean } | null | undefined,
): DisplayRole | null {
  if (!user) return null;
  const roles = user.roles ?? [];
  // A multi-role user gets one chip by highest privilege: admin > reviewer >
  // editor > viewer. platform_admin (Regara-internal) shows ADMIN so we don't
  // leak an internal role string.
  if (roles.includes('admin')) return user.is_founder ? 'OWNER' : 'ADMIN';
  if (roles.includes('platform_admin')) return 'ADMIN';
  if (roles.includes('reviewer')) return 'REVIEWER';
  if (roles.includes('editor')) return 'EDITOR';
  if (roles.includes('viewer')) return 'VIEWER';
  // No recognized role (e.g. a removed/tombstoned user) — no chip rather than
  // silently inheriting ADMIN.
  return null;
}

/** The TEXT for the header/profile role chip. Same as {@link displayRole} for
 * every case EXCEPT a combined editor+reviewer set, which spells out both (so
 * the chip reflects the full role SET like the Team table) rather than
 * collapsing to the single highest-privilege chip. Colour still comes from
 * {@link displayRole} — editor and reviewer share a tone, so the combined chip
 * renders correctly. */
export function displayRoleLabel(
  user: { roles: UserRole[]; is_founder?: boolean } | null | undefined,
): string | null {
  if (!user) return null;
  const roles = user.roles ?? [];
  // Combined non-admin capability set → show both. admin / platform_admin still
  // outrank the combo, so guard on their absence.
  if (
    !roles.includes('admin') &&
    !roles.includes('platform_admin') &&
    roles.includes('editor') &&
    roles.includes('reviewer')
  ) {
    return 'EDITOR & REVIEWER';
  }
  return displayRole(user);
}

export interface MessageResponse {
  message: string;
}

export async function getMe(): Promise<AuthMeResponse> {
  // skipAuthRedirect so the login + signup pages can check anon state
  // without bouncing themselves back to /login on the 401.
  return apiCall<AuthMeResponse>('/api/auth/me', {
    method: 'GET',
    skipAuthRedirect: true,
  });
}

export async function login(args: {
  email: string;
  password: string;
}): Promise<MessageResponse> {
  // skipAuthRedirect so a wrong-password 401 throws back to the form's
  // catch block instead of bouncing the user to /login?next=/login
  // (which full-reloads and drops the alert state).
  return apiCall<MessageResponse>('/api/auth/login', {
    method: 'POST',
    json: args,
    skipAuthRedirect: true,
  });
}

export async function signup(args: {
  email: string;
  access_code: string;
  password: string;
  confirm_password: string;
}): Promise<MessageResponse> {
  return apiCall<MessageResponse>('/api/auth/signup', {
    method: 'POST',
    json: args,
  });
}

export async function logout(): Promise<MessageResponse> {
  return apiCall<MessageResponse>('/api/auth/logout', { method: 'POST' });
}

export async function requestPasswordReset(args: { email: string }): Promise<MessageResponse> {
  return apiCall<MessageResponse>('/api/auth/forgot-password', { method: 'POST', json: args });
}

export async function resetPassword(args: {
  token: string;
  new_password: string;
  confirm_password: string;
}): Promise<MessageResponse> {
  return apiCall<MessageResponse>('/api/auth/reset-password', { method: 'POST', json: args });
}
